import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { Paper } from "@mantine/core";
import { useHotkeys } from "@mantine/hooks";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useNowPlaying, usePublicSettings, useSegment, useSegmentsAt } from "../api/queries";
import { splitRawTitle } from "../api/types";
import type { NowPlaying as NowPlayingTitle, Segment } from "../api/types";
import { useZone } from "../time/useZone";
import { liveEndMs } from "../timeline/geometry";
import { nativeRuntime } from "../platform/native";
import { usePlayer, usePlayheadMs } from "../state/player";
import { latestSegment, neighbourSegment } from "./navigate";
import { NowPlaying } from "./NowPlaying";
import { NUDGE_SECONDS, Transport } from "./Transport";
import { currentAudibleItem, useAudioSession } from "./useAudioSession";
import { useMediaSession } from "./useMediaSession";
import { useNativeSession } from "./useNativeSession";
import { useTapHold } from "./useTapHold";
import { useLayout, type Layout } from "../ui/useLayout";

export { NUDGE_SECONDS } from "./Transport";
export { playbackStatus } from "./NowPlaying";

/**
 * The card's own internal layout, which is the one thing here that is not placement.
 *
 * Placement -- order, width, `flex: none`, which column -- belongs to the `Grid.Col` in
 * `App.tsx`. What is left is three shapes: a column beside the list, a two-row grid on a
 * phone, and a column again in landscape, where the transport and the volume no longer have
 * to share a row.
 *
 * A `style` object and not props because of `safe center`: Mantine's `justify` prop is typed
 * `React.CSSProperties['justifyContent']` and would carry it, but the phone arm is a grid and
 * has no `Flex` to put it on.
 */
function playerStyle(layout: Layout, review: boolean): CSSProperties {
  if (layout.landscape) {
    // Back to a column inside its own track: the transport and the volume sat side by side to
    // save a row, and there is no row to save once the player is not stacked on the list.
    return {
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      justifyContent: "safe center",
      gap: 8,
    };
  }
  if (layout.phone) {
    // Cover and text on one row, every transport control on the next.
    return {
      display: "grid",
      gridTemplateColumns: "auto minmax(0, 1fr)",
      alignItems: "center",
      gap: "8px 10px",
    };
  }
  return {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 14,
    minWidth: 0,
    // The card is what its children are measured against: the cover is as wide as the card
    // and the card is a share of a grid column, which is a number no file here can write
    // down. `inline-size` and not `size`, because the height is exactly what stays fluid.
    containerType: "inline-size",
    // `safe`, because at an impossible height the overflow centring produces goes off the top
    // of the scrollport, where scrolling cannot reach it.
    justifyContent: "safe center",
    minHeight: 0,
    // Beside a two-row queue the card must be allowed to collapse to the queue's height rather
    // than hold the row open; on a short window the transport is scrolled to, never clipped.
    ...(review && layout.twoColumn && { overflowY: "auto" as const }),
  };
}

/**
 * The two lines under the artwork: what is playing, and whoever made it.
 *
 * `described` is the archive row the playhead is inside, `live` what the station is putting out
 * -- passed only while the player is pointed at the broadcast. A track answers "who" with an
 * artist where a show answers it with a billing, so both are tried in turn.
 *
 * The raw title is split on the way through. It is the caption for the minutes between a track
 * starting and the segmenter writing its row, and a jukebox title carries the artist in front of
 * the track: printed whole it put the artist in both lines at once. A live show's raw title is a
 * billing and a place rather than "artist - track", so `is_live` leaves it alone.
 */
export function caption(
  described: Pick<Segment, "title" | "raw_title" | "djs" | "artist" | "city"> | null,
  live: NowPlayingTitle | undefined,
  onAir: boolean,
  hasSource: boolean,
  t: TFunction,
): { title: string; subtitle: string | null } {
  const announced = live?.raw_title && !live.is_live ? splitRawTitle(live.raw_title) : null;
  return {
    title:
      described?.title ??
      // What the station announced, when nothing was parsed out of it. The stream list already
      // shows rows this way, and a raw title is a name where "Archive" is an admission that
      // there is none.
      described?.raw_title ??
      live?.segment?.title ??
      announced?.title ??
      (onAir ? (live?.raw_title ?? t("player.captionLive")) : null) ??
      (hasSource ? t("player.captionArchive") : t("player.captionNothingPlaying")),
    subtitle:
      described?.djs ??
      described?.artist ??
      described?.city ??
      live?.segment?.djs ??
      live?.segment?.artist ??
      announced?.artist ??
      null,
  };
}

/** Move to the neighbouring row in the direction given, and carry on live past the last. */
async function stepTo(direction: -1 | 1): Promise<void> {
  const state = usePlayer.getState();
  const from = state.source?.kind === "segment" ? state.source.segment : null;
  if (!from) {
    // Nothing is playing, or a bare window is: start from the most recent show rather
    // than doing nothing, which reads as a broken button.
    const latest = await latestSegment("show");
    if (latest) state.play({ kind: "segment", segment: latest });
    return;
  }
  const next = await neighbourSegment(direction, from);
  if (next) {
    state.play({ kind: "segment", segment: next });
    return;
  }
  // Nothing after the newest row but the broadcast, which is what carrying on from the
  // newest thing the archive holds means. Without it forward was a dead button on the last
  // row, and that row is where a listener catching up ends up: the track on air has no row
  // of its own until the next segmentation pass writes one, so the block the timeline draws
  // for it (timeline/ghost) is unreachable by stepping.
  if (direction === 1) state.play({ kind: "live" });
}

/** `review`: the label only -- an editor's column is the player, not something to tune in to. */
export function Player({ review = false }: { review?: boolean }) {
  const zone = useZone();
  const layout = useLayout();
  const { t } = useTranslation();
  const native = nativeRuntime() !== null;
  useNativeSession();
  const source = usePlayer((s) => s.source);
  const atLive = usePlayer((s) => s.atLive);
  const playing = usePlayer((s) => s.playing);
  const loading = usePlayer((s) => s.loading);
  const error = usePlayer((s) => s.error);
  const positionMs = usePlayer((s) => s.positionMs);
  // What the readout says, which is not always where the audio is: at the live edge with nothing
  // playing, or with a live stream that is not reporting, the playhead is the clock. Taking
  // `positionMs` here left this line frozen at the instant the page loaded while the timeline's
  // own playhead -- the same playhead -- carried on.
  const playheadMs = usePlayheadMs();
  const volume = usePlayer((s) => s.volume);
  const muted = usePlayer((s) => s.muted);
  const selectedId = usePlayer((s) => s.selectedId);
  const nativeSnapshot = usePlayer((s) => s.nativeSnapshot);
  const { data: nowPlaying } = useNowPlaying();

  const current: Segment | null = native || source?.kind !== "segment" ? null : source.segment;
  // Window playback -- a scrub of the timeline, or a click on bare archive -- has no segment
  // of its own, so what it is playing through has to be looked up by the clock.
  const covering = useCoveringSegment(!native && source?.kind === "window", positionMs);
  // The block the timeline dropped the playhead onto. It resolves that row into the query
  // cache before it switches the source, so this is a cache hit on the very first render of
  // the new window -- which is what stops the placeholder title appearing for the length of
  // `useCoveringSegment`'s round trip. Only while it still covers the playhead: playing on
  // into the next track is the lookup's job, and a selection made somewhere else entirely
  // (a queue row, a search result) describes nothing about what is audible.
  const { data: landed } = useSegment(!native && source?.kind === "window" ? selectedId : null);
  const described = current ?? covering ?? (coversMs(landed, positionMs) ? landed : null);

  // No end-of-source handler, and nothing here that advances on one: the server walks the
  // queue across show boundaries inside a single response body, so the element never reaches
  // an end. Stepping is what a listener asks for, and that is the button above.
  const { audioRef, nudge: nudgeBrowser } = useAudioSession();
  const nudge = useCallback(
    (seconds: number) => {
      if (native) usePlayer.getState().requestNudge(seconds);
      else nudgeBrowser(seconds);
    },
    [native, nudgeBrowser],
  );
  const step = useCallback(
    (direction: -1 | 1) => {
      if (native) usePlayer.getState().requestStep(direction);
      else void stepTo(direction);
    },
    [native],
  );

  const back = useTapHold(
    () => nudge(-NUDGE_SECONDS),
    () => step(-1),
  );
  const forward = useTapHold(
    () => nudge(NUDGE_SECONDS),
    () => step(1),
  );

  const toggle = useCallback(() => {
    const state = usePlayer.getState();
    if (!state.source) {
      // Where the playhead already is, which after a reload is where the listener left off.
      // Falling through to live regardless threw away a restored archive position the
      // instant they pressed play.
      state.play(state.atLive ? { kind: "live" } : { kind: "window", startMs: state.positionMs });
      return;
    }
    if (state.wantsPlayback) {
      state.pause();
      if (!native) audioRef.current?.pause();
    } else {
      state.resume();
    }
  }, [audioRef, native]);

  // Modifiers are matched exactly, so the shifted arrows and the bare ones never both fire,
  // and text fields are skipped for us -- the search box lives on the same screen.
  // `preventDefault` is on by default, so Space still does not page-scroll. Shift+Space no
  // longer toggles: it was never asked for, only inherited from testing the key alone.
  useHotkeys([
    ["space", () => toggle()],
    ["ArrowLeft", () => nudge(-NUDGE_SECONDS)],
    ["ArrowRight", () => nudge(NUDGE_SECONDS)],
    ["shift+ArrowLeft", () => step(-1)],
    ["shift+ArrowRight", () => step(1)],
  ]);

  // Pointed at the broadcast: playing it, or sitting idle at the live edge, which is where a
  // page that has just loaded starts and where pausing the live stream puts you back. Idle
  // counts because the readout beside this is the live instant either way -- what is missing
  // there is the audio, not the subject. Not `atLive` on its own: wheeling to the edge with an
  // archive show still playing leaves the card describing the show, which is what is audible.
  const onAir = native
    ? (nativeSnapshot?.atLive ?? false)
    : source?.kind === "live" || (source === null && atLive);
  // What the station is putting out, which describes the player only while the player is
  // pointed at it. Reaching for it regardless is how an archive window with no name yet ended
  // up captioned with whoever happens to be on air.
  const live = onAir ? nowPlaying : undefined;

  const fallback = caption(described, live, onAir, source !== null, t);
  const browserAudible = native ? null : currentAudibleItem()?.item;
  const title = nativeSnapshot?.audible?.title ?? browserAudible?.title ?? fallback.title;
  const subtitle =
    nativeSnapshot?.audible?.subtitle ??
    (browserAudible ? (browserAudible.artist ?? browserAudible.djs) : fallback.subtitle);
  // Unconditionally, on its own line: `??` short-circuits, so calling this inside the chain
  // below would skip the hook on every render where the show has a cover of its own.
  const defaultCover = usePublicSettings().data?.default_cover_url ?? null;
  // The operator's default last, so it dresses a show with no cover — here and, through
  // useMediaSession below, in the OS media notification as well.
  const artworkUrl = native
    ? (nativeSnapshot?.audible?.artworkPath ?? defaultCover)
    : (described?.artwork_url ?? live?.segment?.artwork_url ?? defaultCover);

  useMediaSession({
    audio: audioRef,
    title,
    artist: subtitle,
    artworkUrl,
    toggle,
    step,
    seek: nudge,
    nudgeSeconds: NUDGE_SECONDS,
  });
  return (
    <Paper
      component="section"
      p="sm"
      style={playerStyle(layout, review)}
      aria-label={t("player.regionLabel")}
    >
      <NowPlaying
        review={review}
        layout={layout}
        t={t}
        title={title}
        subtitle={subtitle}
        artworkUrl={artworkUrl}
        dimmed={current ? !current.playable : false}
        playheadMs={playheadMs}
        zoneName={zone.name}
        durationSeconds={
          native
            ? nativeSnapshot?.audible?.itemDurationUs === null ||
              nativeSnapshot?.audible?.itemDurationUs === undefined
              ? null
              : nativeSnapshot.audible.itemDurationUs / 1_000_000
            : (described?.seconds ?? null)
        }
        onAir={onAir}
        error={error}
        sourceKind={
          native
            ? nativeSnapshot?.source?.kind === "segment"
              ? "segment"
              : nativeSnapshot?.source?.kind === "live" && nativeSnapshot.atLive
                ? "live"
                : nativeSnapshot?.source
                  ? "window"
                  : null
            : source?.kind === "live"
              ? "live"
              : source?.kind === "segment"
                ? "segment"
                : source
                  ? "window"
                  : null
        }
        playing={playing}
        loading={loading}
        muted={muted}
      />
      <Transport
        layout={layout}
        t={t}
        playing={playing}
        loading={loading}
        muted={muted}
        volume={volume}
        expired={current !== null && !current.playable}
        back={back}
        forward={forward}
        toggle={toggle}
      />

      {/* Not `controls`: the transport above is the interface, and a second set of native
          buttons would be a second source of truth for play state. */}
      {/* `auto`, not `none`: the source is one progressive response with no playlist behind
          it, so read-ahead is the only thing there is to survive a stall on. */}
      {!native && <audio ref={audioRef} preload="auto" />}
    </Paper>
  );
}

/**
 * How far the playhead may travel through unlabelled archive before asking again.
 *
 * Only reached when nothing covers the instant -- a gap, or a stretch segmentation has not
 * caught up with -- where re-asking on every timeupdate would be a request every quarter
 * second for an answer that is not going to change.
 */
const RE_RESOLVE_MS = 20_000;

/** The question this lookup is currently asking, and what came back for it. */
export interface CoveringAnchor {
  ms: number;
  known: Segment | null;
}

/**
 * Which instant to ask about, given what the last question returned.
 *
 * Deliberately sticky. Asking about the playhead itself would key a fresh request on every
 * timeupdate, so an answer that still covers the playhead keeps the question it was asked --
 * which is why crossing into the next track costs one request and playing through it costs
 * none.
 *
 * It moves the instant it stops covering, and it moves during the render that notices rather
 * than in an effect afterwards. That is the difference between a name that is already on
 * screen when the frame is painted and one that arrives a frame later: effects run after the
 * paint, so an answer sitting in the cache all along still cost a frame of placeholder --
 * every time a landing switched or seeked to an instant the old anchor did not cover.
 */
export function coveringAnchorMs(anchor: CoveringAnchor | null, positionMs: number): number {
  if (anchor === null) return positionMs;
  if (coversMs(anchor.known, positionMs)) return anchor.ms;
  // Nothing covered the anchor either -- a gap, a stretch segmentation has not caught up with,
  // or an answer still on its way -- and re-asking on every timeupdate would be a request every
  // quarter second for an answer that is not going to change. So the playhead has to travel
  // before the question does.
  if (anchor.known === null && Math.abs(positionMs - anchor.ms) < RE_RESOLVE_MS) return anchor.ms;
  return positionMs;
}

/**
 * The segment the playhead is inside, for sources that do not name one themselves.
 *
 * The instant is resolved by whoever starts window playback (see `fetchSegmentsAt`), so on the
 * paths that matter this is a cache hit rather than a round trip.
 */
function useCoveringSegment(active: boolean, positionMs: number): Segment | null {
  const [anchor, setAnchor] = useState<CoveringAnchor | null>(null);
  const askMs = active ? coveringAnchorMs(anchor, positionMs) : null;
  const { data } = useSegmentsAt(askMs);

  const covering = useMemo(
    () => (askMs === null ? null : pickCovering(data?.items ?? [], askMs)),
    [data, askMs],
  );

  useEffect(() => {
    if (askMs === null) {
      if (anchor !== null) setAnchor(null);
      return;
    }
    // What the render above asked, and what came back for it: between them they are everything
    // the rule needs to decide whether the next render may keep the question.
    if (anchor?.ms !== askMs || anchor.known !== covering)
      setAnchor({ ms: askMs, known: covering });
  }, [anchor, askMs, covering]);

  // Only while the answer still describes where the playhead is: a stale name over the next
  // track is worse than no name at all.
  if (!active) return null;
  return coversMs(covering, positionMs) ? covering : null;
}

/**
 * Whether a segment's span contains an instant. The end is exclusive, as everywhere else.
 *
 * A show still on air reaches the clock, by the same rule the timeline draws it to
 * (`liveEndMs`): its stored end is only as new as the last segmenter pass, so a landing in the
 * last minute of the broadcast fell outside the very block the centre bar was standing on and
 * the player captioned it "Archive". One reading of what a row covers, shared by the canvas
 * that selects it and the pane that names it.
 */
export function coversMs(segment: Segment | null | undefined, ms: number): segment is Segment {
  return !!segment && Date.parse(segment.start) <= ms && liveEndMs(segment, Date.now()) > ms;
}

/**
 * Which of the segments over an instant to name.
 *
 * A track and the show containing it both cover it. The track is the more specific answer
 * and matches what the timeline puts under the pointer, tracks being drawn over shows.
 */
export function pickCovering(items: Segment[], ms: number): Segment | null {
  const over = items.filter((item) => coversMs(item, ms));
  return (
    over.find((item) => item.kind === "track" && item.title) ??
    over.find((item) => item.kind === "track") ??
    over.find((item) => item.kind === "show") ??
    null
  );
}
